import { useState, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';

export interface ToastProps {
  message: string;
  duration?: number;
  isVisible: boolean;
  onClose: () => void;
}

export function Toast({
  message,
  duration = 1500,
  isVisible,
  onClose
}: ToastProps) {
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={cn(
        "fixed z-50",
        "text-center",
        "transition-all duration-300",
        isVisible ? "opacity-100" : "opacity-0",
        "px-4"
      )}
      style={{
        bottom: '106px',
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '8px 16px',
        borderRadius: '40px',
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: '#FFFFFF',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '1.4',
        width: 'auto',
        minWidth: 'min-content',
        maxWidth: '340px',
        wordBreak: 'keep-all',
        textAlign: 'center',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}
    >
      {message}
    </div>
  );
}

// Toast Hook
export function useToast() {
  const [toast, setToast] = useState<{
    message: string;
    isVisible: boolean;
    duration?: number;
  } | null>(null);

  const showToast = useCallback((message: string, duration: number = 2500) => {
    setToast({ message, isVisible: true, duration });
    
    setTimeout(() => {
      setToast(null);
    }, duration);
  }, []);

  const ToastContainer = () => {
    if (!toast) return null;
    
    return (
      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={() => setToast(null)}
        duration={toast.duration ?? 2500}
      />
    );
  };

  return {
    showToast,
    ToastContainer
  };
}
